import { Input } from "@/components/ui/input";
import { X as XIcon } from "lucide-react";
import { TaxHiddenBadge } from "@/components/common/TaxHiddenBadge";
import { TaxOverrideBadge } from "@/components/common/TaxOverrideBadge";
import { useCanOverrideTax } from "@/components/common/TaxOverrideDialog";

// Totals block on the WO form: Subtotal (or Original / Change Orders /
// Revised when COs exist) → CC Fee editor → Tax row → Grand Total.
// Read-only for non-draft WOs.
//
// All money figures arrive pre-calculated from the parent (subtotal,
// coSubtotal, revisedSubtotal, ccFeeAmount, taxAmount, total) so this
// component is purely presentational. Extracted from WorkOrderTab.jsx
// (Feb 2026, iter177).
export function WOTotalsRow({
  form, setForm,
  isEditable,
  subtotal, coSubtotal, revisedSubtotal,
  ccFeeAmount, taxAmount, total,
  taxOverride, openTaxOverride, closeTaxOverride, setTaxOverride,
  manualTax, manualTaxStale, onManualTaxChange, woId,
  fmt,
}) {
  const canOverrideTax = useCanOverrideTax();
  return (
    <div className="flex justify-end" data-testid="wo-totals-row">
      <div className="w-72 space-y-2 text-sm">
        {/* CC fee is BAKED into the displayed subtotal — it rides on
            material if any, else equipment, else labor. The customer
            never sees a separate "CC Fee" line on the quote or invoice;
            it's folded into the category totals. */}
        {coSubtotal > 0 ? (
          <>
            <div className="flex justify-between"><span className="text-slate-500">Original Subtotal</span><span>{fmt(subtotal + ccFeeAmount)}</span></div>
            <div className="flex justify-between text-amber-700"><span>Change Orders</span><span>+{fmt(coSubtotal)}</span></div>
            <div className="flex justify-between font-medium border-t pt-1"><span>Revised Subtotal</span><span>{fmt(revisedSubtotal + ccFeeAmount)}</span></div>
          </>
        ) : (
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmt(subtotal + ccFeeAmount)}</span></div>
        )}
        {Number(form.discount_amount || 0) > 0 && (
          <div className="rounded px-2 py-1 bg-slate-50 text-[11px] text-slate-500" data-testid="wo-discount-stamp">
            <div className="flex justify-between font-medium text-slate-600">
              <span>Proposal discount{String(form.discount_type || "") === "pct" ? ` (${form.discount_value}%)` : ""}</span>
              <span>−{fmt(Number(form.discount_amount))}</span>
            </div>
            <div>Already baked into the line items above — it never prints as its own line.</div>
          </div>
        )}
        <div className="space-y-1">
          {/* Editor-only: expose the CC fee percentage so AMs can tune
              or zero it out. Customer-facing view does NOT render this
              row; see the hide gate below. */}
          {isEditable && (form.cc_fee_pct == null || form.cc_fee_pct > 0) && (
            <div className="flex items-center gap-3 text-[11px] bg-slate-50 rounded px-2 py-1">
              <span className="text-slate-400 flex-1">CC Fee (baked into subtotal)</span>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={form.cc_fee_pct == null ? 0 : form.cc_fee_pct}
                  onChange={(e) => setForm({ ...form, cc_fee_pct: parseFloat(e.target.value) || 0 })}
                  className="h-7 w-20 text-xs text-right tabular-nums"
                  step="0.1"
                  data-testid="input-wo-cc-fee"
                />
                <span className="text-slate-400">%</span>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, cc_fee_pct: 0 })}
                  className="text-slate-300 hover:text-red-500 ml-1"
                  title="Remove CC fee"
                  data-testid="btn-remove-cc-fee"
                ><XIcon className="w-3 h-3" /></button>
                <span className="ml-2 min-w-[60px] text-right text-slate-500">{fmt(ccFeeAmount)}</span>
              </div>
            </div>
          )}
          {isEditable && form.cc_fee_pct === 0 && (
            <button
              type="button"
              onClick={() => setForm({ ...form, cc_fee_pct: 3.5 })}
              className="text-[11px] text-blue-600 hover:underline"
              data-testid="btn-add-cc-fee"
            >+ Add CC Fee (3.5%)</button>
          )}
          <div className="flex items-center gap-3">
            <span className="text-slate-500 text-sm flex-1">Tax</span>
            <div className="flex items-center gap-1">
              {isEditable ? (
                <>
                  <Input
                    type="number"
                    value={form.tax_rate || ""}
                    onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                    className="h-7 w-20 text-sm text-right tabular-nums"
                    step="0.1"
                    data-testid="input-wo-tax"
                  />
                  <span className="text-slate-500">%</span>
                </>
              ) : (
                taxOverride ? (
                  <>
                    <Input
                      type="number"
                      autoFocus
                      value={form.tax_rate || ""}
                      onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                      onBlur={closeTaxOverride}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "Escape") {
                          e.preventDefault();
                          setTaxOverride(false);
                        }
                      }}
                      className="h-7 w-20 text-sm text-right tabular-nums"
                      step="0.01"
                      data-testid="input-wo-tax-override-totals"
                    />
                    <span className="text-slate-500">%</span>
                  </>
                ) : (
                  <span
                    /* #128 (Group G): non-draft (read-only) WOs must
                       not toggle tax override. Once a WO is submitted
                       /approved/scheduled the tax rate is locked in
                       along with the rest of the financials. */
                    className={`text-slate-500 ${(isEditable && !manualTax) ? "cursor-pointer hover:bg-amber-50 px-1 -mx-1 rounded select-none" : ""}`}
                    title={(isEditable && !manualTax) ? "Double-click to manually override the tax rate" : manualTax ? "Tax is manually set — clear it below to edit the rate" : ""}
                    onDoubleClick={() => { if (isEditable && !manualTax) openTaxOverride(); }}
                    data-testid="wo-tax-rate-readonly"
                  >
                    {form.tax_rate}%
                  </span>
                )
              )}
              <span className="ml-2 min-w-[80px] text-right" data-testid="wo-tax-amount">{fmt(taxAmount)}</span>
            </div>
          </div>
          {form.hide_tax && (
            <div className="flex justify-end pt-0.5" data-testid="wo-totals-tax-hidden">
              <TaxHiddenBadge taxAmount={taxAmount} taxRate={form.tax_rate} testId="wo-totals-tax-hidden-badge" />
            </div>
          )}
          {manualTax && (
            <div className="flex justify-end pt-0.5">
              <TaxOverrideBadge override={manualTax} stale={manualTaxStale} docType="work_order" docId={woId}
                canManage={canOverrideTax && !!woId} onChange={onManualTaxChange} testId="wo-tax-override-badge" />
            </div>
          )}
        </div>
        <div className="flex justify-between font-bold text-base border-t pt-1">
          <span>Total</span><span data-testid="wo-total">{fmt(total)}</span>
        </div>
        {form.tax_absorbed?.amount > 0 && (
          <div
            className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
            data-testid="wo-tax-absorbed-warning"
          >
            <div className="font-semibold">
              Tax absorbed — {fmt(Number(form.tax_absorbed.amount))}, margin reduced
            </div>
            <div>
              The signed proposal quoted no tax, so jurisdiction tax ({form.tax_absorbed.rate}%
              {form.tax_absorbed.tax_no ? ` · ${form.tax_absorbed.tax_no}` : ""}) sits inside the
              signed total of {fmt(Number(form.tax_absorbed.signed_total || 0))}. Tax included in
              material. Internal note only — never shown to the customer.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
